import { Controller } from 'react-hook-form';
import { FormProps } from '../types';
import { Input } from 'antd';
import { ChangeEvent, ReactNode } from 'react';
import parsePhoneNumberFromString, {
  isPossiblePhoneNumber,
} from 'libphonenumber-js/max';

export function FormPhone({
  t,
  control,
  name,
  label,
  placeholder,
  disabled,
  error,
  required,
}: FormProps) {
  const getErrorMessage = () => {
    if (!error) return '';

    if (typeof error.message === 'string') {
      return error.message;
    }

    if (error.message) {
      return t(String(error.message), { name: t(`forms.${name}`) });
    }

    return '';
  };

  const invalidMessage = () =>
    t('errors.formErrors.isNotValid', { name: t(`forms.${name}`) });

  const validatePhone = (value: string) => {
    if (!value) return true;
    const normalized = value.replace(/[^\d+]/g, '');
    const phone = parsePhoneNumberFromString(normalized);
    if (!phone) {
      return invalidMessage();
    }
    const isValid = phone.isValid() && isPossiblePhoneNumber(normalized);
    if (!isValid) {
      return invalidMessage();
    }
    const type = phone.getType();
    if (type !== 'MOBILE') {
      return invalidMessage();
    }

    return true;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    let value = e.target.value.trim();

    if (!value) {
      onChange('');
      return;
    }

    value = value.replace(/[^+\d]/g, '');
    value = value.replace(/^(\++)/, '+');
    value = value.replace(/(?!^)\+/g, '');
    if (value[0] !== '+') {
      value = '+' + value.replace(/\+/g, '');
    }

    onChange(value);
  };

  return (
    <div className='flex flex-col gap-1'>
      {label && (
        <label>
          {typeof label === 'string' ? t(label) : (label as ReactNode)}
        </label>
      )}

      <Controller
        control={control}
        name={name}
        rules={{
          required: required
            ? {
                value: true,
                message:
                  typeof required === 'string'
                    ? required
                    : t('errors.formErrors.required', {
                        name: t(`forms.${name}`),
                      }),
              }
            : false,
          validate: validatePhone,
        }}
        render={({ field }) => (
          <>
            <Input
              {...field}
              size='large'
              placeholder={t(placeholder ?? '+90XXXXXXXXXX')}
              disabled={disabled}
              maxLength={16}
              status={error ? 'error' : ''}
              onChange={(e) => handleChange(e, field.onChange)}
              className={`h-14 rounded-2xl border-none border ${error ? 'border-red-500' : 'border-gray-300'} placeholder-gray-400 dark:placeholder-gray-500`}
            />
            {error && (
              <span className='text-red-500 text-xs mt-1'>
                {getErrorMessage()}
              </span>
            )}
          </>
        )}
      />
    </div>
  );
}
